import apiClient from '../../shared/services/apiClient';

/**
 * Support Ticket Service (User Version)
 *
 * All routes use /api/user/support/* prefix.
 * Responses: { status: 'success'|'error', msg, data }
 */
export const supportService = {
    /** Departments, linkable services, priorities, upload limits */
    getOptions: async () => {
        try {
            const response = await apiClient.get('/api/user/support/options');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** @param {Object} params - { status, page, per_page } */
    getTickets: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/user/support/tickets', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    getTicket: async (id) => {
        try {
            const response = await apiClient.get(`/api/user/support/tickets/${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** Presigned upload for one attachment: { name, size } → { url, key, headers, max_size } */
    getUploadUrl: async (file) => {
        try {
            const response = await apiClient.post('/api/user/support/uploads', file);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * @param {Object} data - { department_id, subject, body, priority, service_type, resource_id }
     * @param {Array} attachments - upload keys
     */
    createTicket: async (data, attachments = []) => {
        try {
            const response = await apiClient.post('/api/user/support/tickets', { ...data, attachments });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /** Replying to a closed ticket reopens it */
    reply: async (id, body, attachments = []) => {
        try {
            const response = await apiClient.post(`/api/user/support/tickets/${id}/reply`, { body, attachments });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    closeTicket: async (id) => {
        try {
            const response = await apiClient.post(`/api/user/support/tickets/${id}/close`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Download an attachment (needs auth header → blob + object URL).
     * Throws { msg } when the server answers with a JSON error instead of the file.
     */
    downloadAttachment: async (attachment) => {
        let response;
        try {
            response = await apiClient.get(`/api/user/support/attachments/${attachment.id}`, { responseType: 'blob' });
        } catch (error) {
            throw error.response?.data || error;
        }
        const blob = response.data;
        if (blob && blob.type && blob.type.includes('application/json')) {
            let parsed = null;
            try { parsed = JSON.parse(await blob.text()); } catch (_) { /* not JSON: treat as file */ }
            if (parsed && parsed.status === 'error') throw parsed;
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.filename || `attachment-${attachment.id}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    },
};

export default supportService;
